import { CertificateStatus, ICertificate, ICertificateDAO } from './certificate';
import { IBaseEntity } from './base';
import { IRoleConfig, Role } from './role';
import { IVariableDAO, IVariableOption, VariableType } from './variable';
import { IKeyArray } from 'types/IKeyArray';
import { ICoursePermission, ICourseProgressDAO } from './permissions';
import { IName } from 'types/session/auth';
import { ICourseNodeDAO, ICourseVariableDAO } from './course';
import { IText } from './text';
import { IImage } from './image';
import { IGroupUser, IGroupUserDAO } from './group';

export interface IUser extends IBaseEntity {
  email: string;
  password: string;
}

export interface IOrganisationUser extends IBaseEntity {
  signin: IOrganisationUserSignin;
  role: Role;
  roleConfigId: number;
  googleId: number;
  fbId: number;
  liId: number;
  firstName: string;
  lastName: string;
  email: string;
  cookies: boolean;
  verifiedEmail: boolean;
  account: boolean;
  verifiedID: boolean;
  systemId: string;
  signinId: number;
  avatar: string;
  languageId: string;
  report?: boolean;
  __stale?: boolean;
}

export interface IOrganisationUserDAO extends IOrganisationUser {
  roleConfig?: IRoleConfig;
  groupUserId?: number;
  name?: string;
  certificates?: IOrgUserCertificate[];
  certificateReports?: IKeyArray<IOrgUserCertificateReport>;
  organisationUserVariables?: IKeyArray<IOrgUserVariableDAO | IOrgUserVariable>;
  variableOverrides?: IOrgUserVariableOverride[];
  groupUsers?: IGroupUserDAO[];
  accessPermissions?: ICourseProgressDAO[];
}

export interface IOrganisationUserReport extends IOrganisationUserDAO {
  lastVariable?: IOrgUserVariable;
  lastCertificate?: IOrgUserCertificateReport;
  lastAccessPermission?: any;
}

export interface IOrganisationUserSignin extends IBaseEntity {
  lastLoginSigninId: number;
  lastLoginSignin: IAuditSignin;
}

export interface IAuditSignin extends IBaseEntity {
  deviceType: number;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  city: string;
  countryCode: string;
  latitude: number;
  longitude: number;
}

export interface IOrganisationUserBasicDAO extends IBaseEntity {
  first: string;
  last: string;
  email: string;
  avatar: string;
}

export interface IOrgUserCertificate extends IBaseEntity {
  organisationUserId: number;
  certificateId: number;
  status: CertificateStatus;
  token: string;
  issuedDate: string;
  expiryDate: string;
  url: string;
}

export interface IOrgUserCertificateDAO extends IOrgUserCertificate {
  certificate: ICertificate;
  name: IName;
}

export interface IOrgUserCertificateReport extends IOrgUserCertificate {
  organisationUser: IOrganisationUserReport | IOrganisationUserDAO;
  certificate: ICertificateDAO;
  progress: number;
  organisationUserVariables: IOrgUserVariableDAO[];
  variableGoals: IOrgUserCourseVariable[];
}

export interface IOrgUserCourseVariable {
  complete: boolean;
  variable: ICourseVariableDAO;
  orgUserVariable: IOrgUserVariable;
  orgUserVariableOverride: IOrgUserVariableOverride;
}

export interface IOrgUserCertificateDetailDAO extends IOrgUserCertificateDAO {
  certificate: ICertificateDAO;
  url: string;
  entities: IOrgUserCertificateDetailEntitiesDAO;
}

export interface IOrgUserCertificateDetailEntitiesDAO extends IBaseEntity {
  name: string;
  type: VariableType;
  value: number;
  target: number;
}

export interface IOrgUserState extends IBaseEntity {
  courseId: number;
  moduleInstanceId: number;
  resourceInstanceId: number;
  actionId: number;
}

export interface IOrgUserVariable extends IBaseEntity {
  organisationUserId: number;
  variableId: number;
  pass: boolean;

  value: number;
  attribute: string;
  variableOptionId: number;
  onStandown: boolean;
  courseProgressId: number;
  courseNodeId: number;
  textId: number;
  imageId: number;
  receiptUrl?: string;
  audit?: IOrgUserVariableAudit[];
}

export interface IOrgUserVariableDAO extends IOrgUserVariable {
  organisationUser: IOrganisationUser;
  courseNode?: ICourseNodeDAO;
  variable: IVariableDAO;
  option?: IVariableOption;
  attribute: any;
  text?: IText;
  image?: IImage;
}

export interface IOrgUserVariableOverride extends IBaseEntity {
  organisationUserId: number;
  variableId: number;
  value: number;
  pass: boolean;
  variableOptionId: number;
}

export interface IOrgUserVariableAudit extends IBaseEntity {
  orgUserVariableId: number;
  courseOutId: number;
  moduleOutId: number;
  value: number;
}

export enum DeviceType {
  DESKTOP = 0,
  MOBILE = 1,
  TABLET = 2,
}

export enum UserStatus {
  DELETED = -1,
  ACTIVE = 0,
  INACTIVE = 1,
}

export enum UserType {
  STANDARD = 0,
  SYSTEM = 1,
  UNKOWN = 2,
}

export interface IOrgUserCourse extends IOrganisationUserDAO {
  courseId: number;
  courseProgress: ICourseProgressDAO;
  coursePermission: ICoursePermission;
  date: string;
}
